import { PAINT_RADIUS_DEG, type PaintPixels } from './mindPaint.ts';

/** Steady white traveling-wave stimuli, in visual-field degrees (right/up).
 * The endpoint grid is the same local square used by Paint and the sampler. */
export type WaveKind = 'wedge' | 'ring';
export const WAVE = {
  wedge: { initial: 45, min: 0, max: 359, step: 1, periodMs: 12_000 },
  ring: { initial: 4, min: 0.5, max: 11.5, step: 0.25, periodMs: 8_000 },
} as const;

const wrap = (value: number, span: number) => ((value % span) + span) % span;

/** Play and manual input land on exactly the same slider steps and pixels. */
export function waveValue(kind: WaveKind, value: number): number {
  return kind === 'wedge' ? wrap(Math.round(value), 360) : Math.max(0.5, Math.min(11.5, Math.round(value * 4) / 4));
}

/** Elapsed time, not frame count: a delayed callback never slows the cycle.
 * The caller retains the unrounded starting phase until Pause or a new source. */
export function waveValueAt(kind: WaveKind, start: number, elapsedMs: number): number {
  const span = kind === 'wedge' ? 360 : 11;
  const low = WAVE[kind].min;
  if (elapsedMs <= 0) return waveValue(kind, start);
  return waveValue(kind, low + wrap(start - low + span * elapsedMs / WAVE[kind].periodMs, span));
}

// Compute the fixed field geometry once, not trigonometry for every Play frame.
let grid: { width: number; height: number; radii: Float64Array; angles: Float64Array } | null = null;
export function drawWave(image: PaintPixels, kind: WaveKind, value: number): void {
  const { width, height, data } = image;
  if (!Number.isFinite(value) || width < 2 || height < 2 || data.length !== width * height * 4) throw new Error('Invalid wave pixels');
  if (!grid || grid.width !== width || grid.height !== height) {
    const radii = new Float64Array(width * height), angles = new Float64Array(width * height);
    for (let row = 0; row < height; row++) for (let col = 0; col < width; col++) {
      const x = (col / (width - 1) - 0.5) * 2 * PAINT_RADIUS_DEG;
      const y = (0.5 - row / (height - 1)) * 2 * PAINT_RADIUS_DEG;
      const i = row * width + col;
      radii[i] = Math.hypot(x, y);
      angles[i] = Math.atan2(y, x) * 180 / Math.PI;
    }
    grid = { width, height, radii, angles };
  }
  for (let i = 0; i < grid.radii.length; i++) {
    const r = grid.radii[i]!;
    const inside = r <= PAINT_RADIUS_DEG && (kind === 'wedge'
      ? r >= 0.5 && Math.abs(wrap(grid.angles[i]! - value + 180, 360) - 180) <= 15
      : Math.abs(r - value) <= 0.5);
    const white = inside ? 255 : 0;
    data[4 * i] = data[4 * i + 1] = data[4 * i + 2] = white;
    data[4 * i + 3] = 255;
  }
}
